// src/components/AcquisitionIntelDashboard.js
import { useState, useRef, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { ChevronDown, ChevronRight, Plus, Map, FileText, Activity, ArrowRight } from 'lucide-react';
import lbIcon from './components/favicon.png';

const AcquisitionIntelDashboard = () => {
  const navigate = useNavigate();
  const [scrolled, setScrolled] = useState(false);
  const [projects, setProjects] = useState([
    {
      id: 1,
      name: 'Project LakewoodRanch',
      regions: [
        {
          id: 1, 
          name: 'Region 1',
          lat: '27.3893° N',
          lon: '82.4517° W',
          totalAcreage: '520.75',
          exclusionAcreage: '85.30',
          inclusionAcreage: '435.45',
          exclusionEntities: 'Wetlands, Protected Forest'
        },
        {
          id: 2, 
          name: 'Region 2',
          lat: '27.3950° N',
          lon: '82.4600° W',
          totalAcreage: '342.10',
          exclusionAcreage: '42.60',
          inclusionAcreage: '299.50',
          exclusionEntities: 'Wetlands, Water Bodies'
        }
      ]
    },
    {
      id: 2,
      name: 'Project Los Angeles',
      regions: [
        {
          id: 1, 
          name: 'Region 1',
          lat: '13.0827° N',
          lon: '80.2707° E',
          totalAcreage: '780.25',
          exclusionAcreage: '125.40',
          inclusionAcreage: '654.85',
          exclusionEntities: 'Urban Areas, Water Bodies'
        },
        {
          id: 2, 
          name: 'Region 2',
          lat: '13.0900° N',
          lon: '80.2800° E',
          totalAcreage: '560.30',
          exclusionAcreage: '95.20',
          inclusionAcreage: '465.10',
          exclusionEntities: 'Protected Areas, Urban Development'
        },
        {
          id: 3, 
          name: 'Region 3',
          lat: '13.0750° N',
          lon: '80.2650° E',
          totalAcreage: '420.15',
          exclusionAcreage: '65.30',
          inclusionAcreage: '354.85',
          exclusionEntities: 'Wetlands, Agricultural Land'
        }
      ]
    },
    {
      id: 3,
      name: 'Project Austin',
      regions: [
        {
          id: 1, 
          name: 'Region 1',
          lat: '30.2672° N',
          lon: '97.7431° W',
          totalAcreage: '630.45',
          exclusionAcreage: '110.25',
          inclusionAcreage: '520.20',
          exclusionEntities: 'Protected Areas, Urban Development'
        }
      ]
    }
  ]);
  
  const [expandedProjects, setExpandedProjects] = useState({});
  const [expandedRegions, setExpandedRegions] = useState({});
  const [showNewProjectModal, setShowNewProjectModal] = useState(false);
  const [newProjectName, setNewProjectName] = useState('');
  const [newProjectArea, setNewProjectArea] = useState('');
  const [showRedirect, setShowRedirect] = useState(false);
  
  const welcomeRef = useRef(null);
  
  useEffect(() => {
    const handleScroll = () => {
      const scrollPosition = window.scrollY;
      if (scrollPosition > 50) {
        setScrolled(true);
      } else {
        setScrolled(false);
      }
    };
    
    window.addEventListener('scroll', handleScroll);
    return () => window.removeEventListener('scroll', handleScroll);
  }, []);
  
  const toggleProject = (projectId) => {
    setExpandedProjects({
      ...expandedProjects,
      [projectId]: !expandedProjects[projectId]
    });
  };
  
  const toggleRegion = (projectId, regionId) => {
    const key = `${projectId}-${regionId}`;
    setExpandedRegions({
      ...expandedRegions,
      [key]: !expandedRegions[key]
    });
  };
  
  const handleCreateProject = () => {
    if (newProjectName.trim() === '') return;
  
    const newProject = {
      id: projects.length + 1,
      name: `Project ${newProjectName}`,
      regions: []
    };
  
    setProjects([...projects, newProject]);
    setShowNewProjectModal(false);
  
    // Redirect to Flask-rendered HTML page with query params
    const externalUrl = `http://127.0.0.1:5000/${newProjectArea}?project_name=${newProjectName}`;
    window.location.href = externalUrl;
  };
  
  
  const goToAnalysis = (projectId, regionId) => {
    navigate(`/analysis?project=${projectId}&region=${regionId}`);
  };
  
  const scrollToProjects = () => {
    window.scrollTo({
      top: welcomeRef.current.offsetHeight,
      behavior: 'smooth'
    });
  };
  
  return (
    <div className="flex flex-col min-h-screen bg-gray-100">
      {/* Navigation Bar */}
      <div className={`bg-white shadow-md px-4 py-2 flex items-center justify-between sticky top-0 z-10 transition-all ${scrolled ? 'border-b border-gray-200' : ''}`}>
        <div className="flex items-center space-x-2">
          <img src={lbIcon} alt="LandBuilder Logo" className="w-9 h-9 object-contain" />
          <div className="font-bold text-lg text-blue-600">Acquisition Intel</div>
        </div>
        <div className="flex items-center space-x-4">
          <button 
            className="flex items-center space-x-1 text-gray-700 hover:text-blue-600"
          >
            <Map size={18} />
            <span>Maps</span>
          </button>
          <button 
            className="flex items-center space-x-1 text-gray-700 hover:text-blue-600"
            onClick={() => navigate('/reports')}>
            <FileText size={18} />
            <span>Reports</span>
          </button>
          <button 
            className="flex items-center space-x-1 text-gray-700 hover:text-blue-600"
          >
            <Activity size={18} />
            <span>Analytics</span>
          </button>
          <button 
            className="bg-blue-600 text-white px-3 py-1 rounded-md hover:bg-blue-700"
            onClick={() => setShowNewProjectModal(true)}
          >
            New Project
          </button>
        </div>
      </div>
      
      {/* Welcome Section */}
      <div 
        ref={welcomeRef}
        className="flex flex-col items-center justify-center h-screen bg-gray-50 px-4"
      >
        <h1 className="text-4xl font-bold text-gray-800 mb-6">Hi Praneeth</h1>
        <p className="text-xl text-gray-600 mb-10 text-center max-w-2xl">
          Welcome to your Acquisition Intel Dashboard. View your project details or create a new analysis.
        </p>
        <button 
          onClick={scrollToProjects}
          className="bg-blue-600 text-white px-6 py-3 rounded-lg hover:bg-blue-700 flex items-center space-x-2 shadow-md"
        >
          <span>View Your Projects</span>
          <ChevronDown size={20} />
        </button>
      </div>
      
      {/* Projects Section */}
      <div className="container mx-auto py-12 px-4">
        <h2 className="text-2xl font-bold text-gray-800 mb-8">Your Projects</h2>
        
        <div className="space-y-6">
          {projects.map((project) => (
            <div key={project.id} className="bg-white rounded-lg shadow-md overflow-hidden">
              <div 
                className="p-4 border-b flex justify-between items-center cursor-pointer hover:bg-gray-50"
                onClick={() => toggleProject(project.id)}
              >
                <h3 className="text-lg font-semibold text-gray-800">{project.name}</h3>
                <div className="flex items-center space-x-2">
                  <span className="text-sm text-gray-500">{project.regions.length} regions</span>
                  {expandedProjects[project.id] ? 
                    <ChevronDown size={20} className="text-gray-500" /> : 
                    <ChevronRight size={20} className="text-gray-500" />
                  }
                </div>
              </div>
              
              {expandedProjects[project.id] && (
                <div className="bg-gray-50 p-4">
                  {project.regions.map((region) => (
                    <div key={region.id} className="mb-2 last:mb-0">
                      <div 
                        className="p-3 bg-white rounded-md shadow-sm flex justify-between items-center cursor-pointer hover:bg-blue-50"
                        onClick={() => toggleRegion(project.id, region.id)}
                      >
                        <span className="font-medium text-gray-700">{region.name}</span>
                        {expandedRegions[`${project.id}-${region.id}`] ?
                          <ChevronDown size={16} className="text-gray-500" /> :
                          <ChevronRight size={16} className="text-gray-500" />
                        }
                      </div>
                      
                      {expandedRegions[`${project.id}-${region.id}`] && (
                        <div className="mt-2 p-4 bg-white rounded-md shadow-sm border border-gray-200">
                          <div className="grid grid-cols-2 gap-4">
                            <div>
                              <p className="text-sm"><span className="font-medium">Lat:</span> {region.lat}</p>
                              <p className="text-sm"><span className="font-medium">Lon:</span> {region.lon}</p>
                              <p className="text-sm"><span className="font-medium">Total Acreage:</span> {region.totalAcreage}</p>
                            </div>
                            <div>
                              <p className="text-sm"><span className="font-medium">Exclusion Region Acreage:</span> {region.exclusionAcreage}</p>
                              <p className="text-sm"><span className="font-medium">Inclusion Region Acreage:</span> {region.inclusionAcreage}</p>
                              <p className="text-sm"><span className="font-medium">Exclusion Entities:</span> {region.exclusionEntities}</p>
                            </div>
                          </div>
                          <div className="mt-4 flex justify-end">
                            <button 
                              className="bg-blue-600 text-white px-3 py-1 text-sm rounded-md hover:bg-blue-700 flex items-center space-x-1"
                              onClick={(e) => {
                                e.stopPropagation();
                                goToAnalysis(project.id, region.id);
                              }}
                            >
                              <span>View Details</span>
                              <ArrowRight size={16} />
                            </button>
                          </div>
                        </div>
                      )}
                    </div>
                  ))}
                  
                  <div className="mt-4">
                    <button className="flex items-center space-x-1 text-blue-600 hover:text-blue-800 text-sm font-medium">
                      <Plus size={16} />
                      <span>Add New Region</span>
                    </button>
                  </div>
                </div>
              )}
            </div>
          ))}
          
          {/* Create Another Project Card */}
          <div 
            className="bg-white rounded-lg shadow-md overflow-hidden border-2 border-dashed border-gray-300 p-6 flex flex-col items-center justify-center cursor-pointer hover:bg-gray-50"
            onClick={() => setShowNewProjectModal(true)}
          >
            <div className="w-12 h-12 rounded-full bg-blue-100 flex items-center justify-center mb-4">
              <Plus size={24} className="text-blue-600" />
            </div>
            <h3 className="text-lg font-semibold text-blue-600 mb-2">Create Another Project</h3>
            <p className="text-sm text-gray-500 text-center">
              Start a new acquisition analysis for your next development area
            </p>
          </div>
        </div>
      </div>
      
      {/* New Project Modal */}
      {showNewProjectModal && (
        <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-20">
          <div className="bg-white rounded-lg shadow-lg p-6 w-full max-w-md">
            <h3 className="text-xl font-bold text-gray-800 mb-4">Create New Project</h3>
            
            <div className="space-y-4">
              <div>
                <label htmlFor="projectName" className="block text-sm font-medium text-gray-700 mb-1">
                  Project Name
                </label>
                <input
                  type="text"
                  id="projectName"
                  placeholder="Enter project name"
                  className="w-full p-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500"
                  value={newProjectName}
                  onChange={(e) => setNewProjectName(e.target.value)}
                />
              </div>
              
              <div>
                <label htmlFor="projectArea" className="block text-sm font-medium text-gray-700 mb-1">
                  Development Area
                </label>
                <input
                  type="text"
                  id="projectArea"
                  placeholder="City, State or Region"
                  className="w-full p-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500"
                  value={newProjectArea}
                  onChange={(e) => setNewProjectArea(e.target.value)}
                />
              </div>
            </div>
            
            <div className="mt-6 flex justify-end space-x-3">
              <button
                className="px-4 py-2 text-gray-600 hover:text-gray-800"
                onClick={() => setShowNewProjectModal(false)}
              >
                Cancel
              </button>
              <button
                className="px-4 py-2 bg-blue-600 text-white rounded-md hover:bg-blue-700"
                onClick={handleCreateProject}
              >
                Create Project
              </button>
            </div>
          </div>
        </div>
      )}
      
      {/* Redirect Message */}
      {showRedirect && (
        <div className="fixed bottom-4 right-4 bg-green-100 border-l-4 border-green-500 p-4 rounded shadow-md max-w-md">
          <div className="flex">
            <div className="flex-shrink-0">
              <svg className="h-5 w-5 text-green-500" viewBox="0 0 20 20" fill="currentColor">
                <path fillRule="evenodd" d="M10 18a8 8 0 100-16 8 8 0 000 16zm3.707-9.293a1 1 0 00-1.414-1.414L9 10.586 7.707 9.293a1 1 0 00-1.414 1.414l2 2a1 1 0 001.414 0l4-4z" clipRule="evenodd" />
              </svg>
            </div>
            <div className="ml-3">
              <p className="text-sm text-green-800">
                Project created successfully! Redirecting to project details...
              </p>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default AcquisitionIntelDashboard;
